// ============================================
// ArchetypeLibrary
// Browse archetype personas organized by vertical
// Grid of PersonaCards with vertical selector
// NO framer-motion — pure CSS, matches working admin page patterns
// ============================================

import { useState, useMemo, useEffect, useCallback } from 'react';
import { Users, Sparkles, Filter, CloudDownload, CloudUpload, RefreshCw } from 'lucide-react';
import { PersonaCard } from './PersonaCard';
import type { PersonaData } from './PersonaCard';
import {
  getEnabledTemplates,
  getTemplatesByCategory,
  loadArchetypesFromFirestore,
  exportBuiltInsToFirestore,
  isFirestoreLoaded,
} from '../../../config/archetypeTemplates';
import type { ArchetypeTemplate } from '../../../config/archetypeTemplates';

// ── Verticals ──

const VERTICALS = [
  { id: 'womens-hormones', label: "Women's Hormones" },
  { id: 'mens-hormones', label: "Men's Hormones" },
  { id: 'thyroid', label: 'Thyroid' },
  { id: 'metabolic', label: 'Metabolic' },
  { id: 'fertility', label: 'Fertility' },
  { id: 'general-wellness', label: 'General Wellness' },
] as const;

type VerticalId = (typeof VERTICALS)[number]['id'];

// ── Convert config templates to display personas ──

function templateToPersonaData(template: ArchetypeTemplate): PersonaData {
  return {
    personaId: `config-${template.id}`,
    name: template.label,
    archetype: template.label,
    demographics: {
      name: template.label,
      age: Math.round((template.ageRange[0] + template.ageRange[1]) / 2),
      sex: template.sex === 'auto' ? 'Female' : template.sex,
      state: 'CA',
    },
    labs: [],
    medications: (template.medications ?? []).filter(
      (m) => !m.name.includes('{{'),
    ),
    conditions: template.conditions ?? [],
    contraindications: (template.contraindications ?? []).filter(
      (c) => !c.includes('{{'),
    ),
    allergens: template.allergens ?? [],
    validationChecks: template.checks.map((c, i) => ({
      checkId: `${template.id}-${String(i + 1).padStart(3, '0')}`,
      check: c.check.replace(/\{\{[^}]+\}\}/g, '[config value]'),
      category: c.category,
      severity: c.severity,
      automatable: c.automatable,
    })),
  };
}

function getPersonasForVertical(
  _verticalId: VerticalId,
  templates: ArchetypeTemplate[],
): PersonaData[] {
  // Templates are already filtered by vertical via getEnabledTemplates
  return templates.map(templateToPersonaData);
}

// ── Component ──

export function ArchetypeLibrary() {
  const [selectedVertical, setSelectedVertical] = useState<VerticalId>('womens-hormones');
  const [generating, setGenerating] = useState(false);
  const [generatedPersonas] = useState<PersonaData[]>([]);
  const [showGenerated, setShowGenerated] = useState(false);
  const [showCategory, setShowCategory] = useState<'all' | 'core' | 'adversarial'>('all');

  // Firestore config loading state
  const [configLoading, setConfigLoading] = useState(false);
  const [configStatus, setConfigStatus] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Load archetypes from Firestore on mount
  useEffect(() => {
    if (isFirestoreLoaded()) return;
    let cancelled = false;
    setConfigLoading(true);
    loadArchetypesFromFirestore()
      .then((result) => {
        if (cancelled) return;
        if (result.loaded > 0) {
          setConfigStatus(`Loaded ${result.loaded} template${result.loaded !== 1 ? 's' : ''} from config`);
          setRefreshKey((k) => k + 1);
        }
      })
      .catch(() => {
        // Firestore unavailable — fall back to built-in templates silently
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleReloadFromFirestore = useCallback(async () => {
    setConfigLoading(true);
    setConfigStatus(null);
    try {
      const result = await loadArchetypesFromFirestore({ force: true });
      setConfigStatus(`Reloaded ${result.loaded} template${result.loaded !== 1 ? 's' : ''} from Firestore`);
      setRefreshKey((k) => k + 1);
    } catch {
      setConfigStatus('Failed to load from Firestore — using built-in templates');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const handleExportToFirestore = useCallback(async () => {
    setConfigLoading(true);
    setConfigStatus(null);
    try {
      const result = await exportBuiltInsToFirestore();
      setConfigStatus(
        `Exported ${result.exported} template${result.exported !== 1 ? 's' : ''}` +
        (result.skipped > 0 ? `, ${result.skipped} already existed` : ''),
      );
    } catch {
      setConfigStatus('Failed to export to Firestore');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const enabledTemplates = useMemo(() => getEnabledTemplates(selectedVertical), [selectedVertical, refreshKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredTemplates = useMemo(() => {
    if (showCategory === 'all') return enabledTemplates;
    return getTemplatesByCategory(showCategory, selectedVertical);
  }, [enabledTemplates, showCategory, selectedVertical, refreshKey]);
  const personas = useMemo(
    () => getPersonasForVertical(selectedVertical, filteredTemplates),
    [selectedVertical, filteredTemplates],
  );
  const verticalLabel = VERTICALS.find((v) => v.id === selectedVertical)?.label || selectedVertical;

  function handleGenerateSuite() {
    setGenerating(true);
    // Simulate generation — real generation requires brand config (Med Co Maker)
    setTimeout(() => {
      setGenerating(false);
    }, 2000);
  }

  return (
    <div className="space-y-6">
      {/* ── Header Row ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-xl font-display text-gold flex items-center gap-2">
            <Users size={20} />
            Archetype Library
          </h3>
          <p className="text-base text-text-secondary mt-1">
            {(showGenerated ? generatedPersonas.length : personas.length)} persona{(showGenerated ? generatedPersonas.length : personas.length) !== 1 ? 's' : ''} in{' '}
            <span className="text-text-primary font-medium">
              {showGenerated ? 'Generated Suite (all verticals)' : verticalLabel}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReloadFromFirestore}
            disabled={configLoading}
            title="Reload archetypes from Firestore config"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-bg-elevated text-text-secondary border border-bg-tertiary font-medium text-sm hover:text-text-primary hover:border-gold/30 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {configLoading ? <RefreshCw size={14} className="animate-spin" /> : <CloudDownload size={14} />}
            Load Config
          </button>
          <button
            onClick={handleExportToFirestore}
            disabled={configLoading}
            title="Export built-in archetypes to Firestore"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-bg-elevated text-text-secondary border border-bg-tertiary font-medium text-sm hover:text-text-primary hover:border-gold/30 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <CloudUpload size={14} />
            Seed Config
          </button>
          <button
            onClick={handleGenerateSuite}
            disabled={generating}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gold text-bg-deep font-medium text-base hover:bg-gold/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Sparkles size={16} />
            {generating ? 'Generating...' : 'Generate Suite'}
          </button>
        </div>
        {generatedPersonas.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowGenerated(false)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                !showGenerated
                  ? 'bg-gold/10 text-gold border border-gold/30'
                  : 'bg-bg-elevated text-text-secondary border border-bg-tertiary hover:text-text-primary'
              }`}
            >
              Demo ({personas.length})
            </button>
            <button
              onClick={() => setShowGenerated(true)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                showGenerated
                  ? 'bg-gold/10 text-gold border border-gold/30'
                  : 'bg-bg-elevated text-text-secondary border border-bg-tertiary hover:text-text-primary'
              }`}
            >
              Generated ({generatedPersonas.length})
            </button>
          </div>
        )}
      </div>

      {/* ── Config Status ── */}
      {configStatus && (
        <div className="px-4 py-2.5 rounded-lg bg-bg-elevated border border-bg-tertiary text-sm text-text-secondary">
          {configStatus}
        </div>
      )}

      {/* ── Vertical Selector ── */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <Filter size={16} className="text-text-muted flex-shrink-0" />
        {VERTICALS.map((v) => (
          <button
            key={v.id}
            onClick={() => setSelectedVertical(v.id)}
            className={`px-4 py-2 rounded-full text-base font-medium whitespace-nowrap transition-colors border ${
              selectedVertical === v.id
                ? 'bg-gold/10 text-gold border-gold/30'
                : 'bg-bg-elevated text-text-secondary border-bg-tertiary hover:text-text-primary hover:border-gold/20'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* ── Category Filter ── */}
      <div className="flex items-center gap-2">
        {(['all', 'core', 'adversarial'] as const).map((cat) => (
          <button
            key={cat}
            onClick={() => setShowCategory(cat)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border capitalize ${
              showCategory === cat
                ? 'bg-gold/10 text-gold border-gold/30'
                : 'bg-bg-elevated text-text-secondary border-bg-tertiary hover:text-text-primary hover:border-gold/20'
            }`}
          >
            {cat === 'all' ? `All (${enabledTemplates.length})` : `${cat} (${getTemplatesByCategory(cat).length})`}
          </button>
        ))}
      </div>

      {/* ── Persona Grid — plain div, no framer-motion ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {(showGenerated ? generatedPersonas : personas).map((persona) => (
          <PersonaCard key={persona.personaId} persona={persona} />
        ))}
      </div>

      {/* ── Empty state ── */}
      {personas.length === 0 && (
        <div className="text-center py-16 bg-bg-card border border-bg-tertiary rounded-xl">
          <Users size={40} className="text-text-muted mx-auto mb-3" />
          <p className="text-base text-text-secondary mb-1">
            No personas for this vertical yet
          </p>
          <p className="text-base text-text-muted">
            Click "Generate Suite" to create archetype personas for {verticalLabel}
          </p>
        </div>
      )}
    </div>
  );
}
